import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDate, addCalendarDays, daysBetween, NOTICE_WINDOW_CALENDAR_DAYS } from '../lib/util';
import { StatusBadge } from '../components/StatusBadge';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { PagingControls } from '../components/PagingControls';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { unionMatching, useCaseQuery } from '../state/useCaseQuery';
import { isNotifiableDecision, notificationContact, notificationContactLabel, undeliveredNoticeReason } from '../state/actions/lifecycle';

export function Notifications() {
  const { cases, now } = useAppData();
  const { setSelectedCase, setModal } = useUIState();

  // H-2: both sections query the full visible population server-side; the
  // store union keeps a just-sent notification's row moving between the
  // sections immediately, refetch-free.
  const pendingQuery = useCaseQuery({ notification: 'pending' });
  const sentQuery = useCaseQuery({ notification: 'sent' });

  const pending = unionMatching(pendingQuery.cases, cases, c => Boolean(isNotifiableDecision(c.decision) && !c.notificationSentAt));
  const sent = unionMatching(sentQuery.cases, cases, c => Boolean(c.notificationSentAt), sentQuery.offset);

  // Client-side v1 search + decision facet layered over the in-memory rows the
  // per-section server queries already returned (server-side filtering is a
  // deferred phase 2). One bar drives both sections: rows are tagged with their
  // section so a single query/facet filters the pending card and sent table,
  // then we partition back.
  const rows = [
    ...pending.map(c => ({ c, section: 'pending' as const })),
    ...sent.map(c => ({ c, section: 'sent' as const })),
  ];
  const { filtered: filteredRows, bar } = useSearchFilter(
    rows,
    // #693 — the resolved notification contact, which is who these rows are
    // about: a pending row is a notice owed to that person.
    ({ c }) => [c.id, c.registrant, notificationContactLabel(c),
      notificationContact(c)?.email, c.decision].filter(Boolean).join(' '),
    [
      {
        id: 'decision',
        label: 'Decision',
        options: [
          { value: '', label: 'All decisions' },
          { value: 'SUCCESSFUL', label: 'Successful' },
          { value: 'UNSUCCESSFUL', label: 'Unsuccessful' },
        ],
        predicate: ({ c }, v) => c.decision === v,
      },
    ],
    { placeholder: 'Search notifications by case, registrant, or notification contact…' },
  );
  const filteredPending = filteredRows.filter(r => r.section === 'pending').map(r => r.c);
  const filteredSent = filteredRows.filter(r => r.section === 'sent').map(r => r.c);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Notification Tracking</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>The registrant must be notified within 30 calendar days of determination</div>
      <SearchFilterBar {...bar} />
      {pending.length > 0 && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>⚠ Pending Notifications ({Math.max(pendingQuery.total, pending.length)})</h3>
          {filteredPending.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <strong>{c.registrant}</strong> — <StatusBadge status={c.decision!} />
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Decision: {fmtDate(c.decisionDate)} · To: {notificationContactLabel(c)}{notificationContact(c) ? ` (${notificationContact(c)!.email})` : ' (no contact)'}</div>
                <DeadlineIndicator deadline={addCalendarDays(c.decisionDate!, NOTICE_WINDOW_CALENDAR_DAYS).toISOString()} label="30-day deadline" now={now} />
              </div>
              <button style={{ ...styles.btn, ...styles.btnGold }} onClick={() => { setSelectedCase(c); setModal('send-notification'); }}>Send Notification</button>
            </div>
          ))}
          {filteredPending.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted }}>No pending notifications match the current filters</div>
          )}
        </div>
      )}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: C.success, marginBottom: 12 }}>✓ Sent Notifications ({Math.max(sentQuery.total, sent.length)})</h3>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Case</th><th style={styles.th}>Registrant</th><th style={styles.th}>Decision</th><th style={styles.th}>Sent</th><th style={styles.th}>Method</th><th style={styles.th}>30-Day Deadline</th>
          </tr></thead>
          <tbody>
            {filteredSent.map(c => (
              <tr key={c.id}>
                <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{c.id}</span></td>
                <td style={styles.td}>{c.registrant}</td>
                <td style={styles.td}><StatusBadge status={c.decision!} /></td>
                <td style={styles.td}>{fmtDate(c.notificationSentAt)}</td>
                {/* #543 — the Method cell is where a recorded-but-undelivered notice
                    is visible in a list, so it carries the warning rather than the
                    neutral info tag a delivered one gets. */}
                <td style={styles.td}>{undeliveredNoticeReason(c)
                  ? <span style={{ ...styles.tag, background: C.dangerLight, color: C.danger }} title="Recorded but not delivered — open the case's Notifications tab">{c.notificationMethod || 'none'} · not sent</span>
                  : <span style={{ ...styles.tag, background: C.infoBg, color: C.info }}>{c.notificationMethod}</span>}</td>
                <td style={styles.td}>{fmtDate(c.notificationDeadline)} {daysBetween(c.notificationSentAt!, c.notificationDeadline!) >= 0 ? <span style={{ color: C.success }}>✓ On time</span> : <span style={{ color: C.danger }}>✗ Late</span>}</td>
              </tr>
            ))}
            {filteredSent.length === 0 && (
              <tr><td colSpan={6} style={{ ...styles.td, color: C.textMuted, textAlign: 'center' }}>{sent.length === 0 ? 'No sent notifications' : 'No sent notifications match the current filters'}</td></tr>
            )}
          </tbody>
        </table>
        <PagingControls offset={sentQuery.offset} limit={sentQuery.limit} total={sentQuery.total} count={sent.length} onOffset={sentQuery.setOffset} label="notifications" />
      </div>
    </div>
  );
}
